import { useEffect, useState, useCallback, useMemo } from "react";
import * as Icon from "react-feather";
import { useNavigate } from "react-router";

import Badge from "../common/Badge";
import Button from "../common/Button";
import Card from "../common/Card";
import Loading from "../common/Loading";
import MessageText from "../common/MessageText";
import Page from "../common/Page";
import Table from "../common/Table";
import APIManager from "../../managers/APIManager";
import { useSelector } from "react-redux";

const STATUS_TO_BADGE = {
  init: { color: "secondary", label: "init" },
  aeWaiting: { color: "orange", label: "processing" },
  created: { color: "info", label: "waiting for processing" },
  generated: { color: "success", label: "generated" },
};

const AIVideoList = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [sortKey, setSortKey] = useState("creationDate");
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' | 'desc'
  const [expandedRow, setExpandedRow] = useState(null);

  const siteId = useSelector((state) => state.site?.selectedSite?.virgulId);
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );

  const fetchVideos = useCallback(() => {
    if (!videoCreation || !siteId) {
      setLoading(false);
      navigate("/");
      return;
    }

    setLoading(true);
    APIManager.getAIVideos(siteId)
      .then((res) => {
        setVideos(res?.data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching AI videos:", error);
        setVideos([]);
        setLoading(false);
      });
  }, [videoCreation, siteId, navigate]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const getValueByKey = (video, key) => {
    switch (key) {
      case "title":
        return (video.title || "").toString();
      case "description":
        return (video.description || "").toString();
      case "templateName":
        return (video.templateName || "").toString();
      case "creationType":
        return (video.creationType || "").toString();
      case "status":
        return (video.status || "").toString();
      case "creationDate": {
        const dateStr = video.createDate || "";
        const [datePart, timePart] = dateStr.split(" ");
        if (!datePart || !timePart) return 0;
        const [day, month, year] = datePart.split(".");
        const [hour, minute] = timePart.split(":");
        if (!day || !month || !year || !hour || !minute) return 0;
        // Create a Date object in local time
        const dateObj = new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute)
        );
        return isNaN(dateObj.getTime()) ? 0 : dateObj.getTime();
      }
      default:
        return "";
    }
  };

  const sortedVideos = useMemo(() => {
    if (!Array.isArray(videos) || !videos.length) return [];
    const copy = [...videos];
    copy.sort((a, b) => {
      const aVal = getValueByKey(a, sortKey);
      const bVal = getValueByKey(b, sortKey);
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = (aVal || "").toString().toLowerCase();
      const bStr = (bVal || "").toString().toLowerCase();
      if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
      if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [videos, sortKey, sortDirection]);

  const dirArrow = sortDirection === "asc" ? "▲" : "▼";

  if (loading) {
    return <Loading />;
  }

  return (
    <Page
      title={
        <div className="sm:flex sm:items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-700 tracking-tight leading-tight">
            AI Videos
          </h2>
          <Button
            className="ml-auto"
            onClick={() => navigate("/ai-video/new")}
            variant="primary"
          >
            New Video
          </Button>
        </div>
      }
    >
      <Card>
        {!videos.length ? (
          <MessageText
            message="there are no AI videos available for your account."
            textAlignment="text-center"
            customClass="py-8"
          />
        ) : (
          <div className="overflow-x-scroll">
            <Table
              headers={[
                { content: "thumbnail", className: "text-left" },
                {
                  content: sortKey === "title" ? `title ${dirArrow}` : "title",
                  className: "text-left",
                  onClick: () => toggleSort("title"),
                },
                {
                  content:
                    sortKey === "description"
                      ? `description ${dirArrow}`
                      : "description",
                  className: "text-left",
                  onClick: () => toggleSort("description"),
                },
                {
                  content:
                    sortKey === "templateName"
                      ? `template ${dirArrow}`
                      : "template",
                  className: "text-left",
                  onClick: () => toggleSort("templateName"),
                },
                {
                  content:
                    sortKey === "creationType"
                      ? `creation type ${dirArrow}`
                      : "creation type",
                  className: "text-left",
                  onClick: () => toggleSort("creationType"),
                },
                {
                  content:
                    sortKey === "status" ? `status ${dirArrow}` : "status",
                  className: "text-left",
                  onClick: () => toggleSort("status"),
                },
                {
                  content:
                    sortKey === "creationDate"
                      ? `creation date ${dirArrow}`
                      : "creation date",
                  className: "text-left",
                  onClick: () => toggleSort("creationDate"),
                },
                { content: "actions" },
              ]}
            >
              {sortedVideos.map((video, index) => (
                <>
                  <Table.Row
                    key={`video-${video.id || index}`}
                    dataKey={String(video.id)}
                  >
                    <Table.Col className="text-left">
                      {video.thumbnail ? (
                        <img
                          src={video.thumbnail}
                          alt={video.title || "Video thumbnail"}
                          className="w-16 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <Icon.Video className="text-gray-400" size={20} />
                        </div>
                      )}
                    </Table.Col>
                    <Table.Col className="text-left">
                      <div className="font-medium text-gray-900">
                        {video.title || "Untitled"}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {video.id}
                      </div>
                    </Table.Col>
                    <Table.Col className="text-left">
                      <div className="max-w-xs truncate text-sm text-gray-700">
                        {video.description || "No description"}
                      </div>
                    </Table.Col>
                    <Table.Col className="text-left">
                      <span className="text-sm text-gray-700">
                        {video.templateName || "Default"}
                      </span>
                    </Table.Col>
                    <Table.Col className="text-left">
                      <span className="text-sm text-gray-700">
                        {video.creationType || "Unknown"}
                      </span>
                    </Table.Col>
                    <Table.Col className="text-left">
                      <Badge
                        color={
                          STATUS_TO_BADGE[video.status]?.color || "secondary"
                        }
                      >
                        {STATUS_TO_BADGE[video.status]?.label || video.status}
                      </Badge>
                    </Table.Col>
                    <Table.Col className="text-left">
                      <span className="text-xs text-gray-600">
                        {video.createDate ? video.createDate : "Unknown date"}
                      </span>
                    </Table.Col>
                    <Table.Col>
                      <div className="flex items-center justify-center space-x-2">
                        <Button
                          className="text-xs"
                          onClick={() =>
                            navigate(`/ai-video/${video.creationId}`)
                          }
                          disabled={
                            video.status === "aeWaiting" ||
                            video.status === "generated"
                          }
                        >
                          <Icon.Edit3 className="inline mr-1" size={14} />
                          <span>Open</span>
                        </Button>
                        <Button
                          className="text-xs"
                          onClick={() => {
                            setExpandedRow((prev) =>
                              prev === index ? null : index
                            );
                          }}
                          disabled={
                            video.status === "aeWaiting" ||
                            video.status === "created" ||
                            video.status === "init"
                          }
                        >
                          <Icon.Play className="inline mr-1" size={14} />
                          <span>
                            {expandedRow === index
                              ? "Hide Video"
                              : "Show Video"}
                          </span>
                        </Button>
                      </div>
                    </Table.Col>
                  </Table.Row>
                  {expandedRow === index && (
                    <Table.Row key={`video-expand-${index}`}>
                      <td colSpan={8} className="px-2 py-2 bg-gray-50">
                        <div className="overflow-hidden transition-all duration-300 ease-in-out">
                          <div
                            className="relative w-full"
                            style={{ paddingBottom: "56.25%" }}
                          >
                            <iframe
                              title={`video-${index}`}
                              src={
                                video.url ||
                                `https://api.empower.net/v3/embed/npm_empowernet/${video.id}?showrel=0&loop=0&autoplay=1&autohide=1&showinfo=1&socialbuttons=0&automute=0`
                              }
                              className="absolute top-0 left-0 w-full h-full rounded"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        </div>
                      </td>
                    </Table.Row>
                  )}
                </>
              ))}
            </Table>
          </div>
        )}
      </Card>
    </Page>
  );
};

export default AIVideoList;
